import * as _ from 'underscore';
import { FeatureFlags } from '../FeatureFlags';
import { Module } from '../Module';
import { DataAlerts } from '../resources/DataAlerts';
import { DataConnections } from '../resources/DataConnections';
import { DataSources } from '../resources/Datasources';
import { Flows } from '../resources/Flows';
import { Projects } from '../resources/Projects';
import { Subscriptions } from '../resources/Subscriptions';
import { Views } from '../resources/Views';
import { Workbooks } from '../resources/Workbooks';
import { ServerService } from '../services/ServerService';
import { ContentFiltersKeys } from './ContentFilters';
import {
  DataAlertContentType,
  DataConnectionContentType,
  DatasourceContentType,
  FlowContentType,
  ProjectContentType,
  SubscriptionContentType,
  ViewContentType,
  WorkbookContentType,
} from './ContentTypes';
import { IPlace, Place, Subplace } from './IPlace';

export class SiteUserPlace {

  private urlParamsFn: () => _.Dictionary<any>;

  private static ExcludeFilterKeys = [
    ContentFiltersKeys.owner
  ];

  public static $inject = [
    '$location',
    '$q',
    'DataAlerts',
    'DataConnections',
    'DataSources',
    'Flows',
    'Projects',
    'ServerService',
    'Subscriptions',
    'Views',
    'Workbooks',
  ];

  constructor(
    private $location: ng.ILocationService,
    private $q: ng.IQService,
    private DataAlerts: DataAlerts,
    private DataConnections: DataConnections,
    private DataSources: DataSources,
    private Flows: Flows,
    private Projects: Projects,
    private serverService: ServerService,
    private Subscriptions: Subscriptions,
    private Views: Views,
    private Workbooks: Workbooks,
  ) {
    this.urlParamsFn = () => this.$location.search();
  }

  public create(ownerId: string): IPlace {
    const place = new Place('siteUser');

    place.addSubplace(Subplace.create(
      'projects',
      ProjectContentType,
      this.Projects,
      this.urlParamsFn,
      { owner: ownerId },
      SiteUserPlace.ExcludeFilterKeys));

    place.addSubplace(Subplace.create(
      'workbooks',
      WorkbookContentType,
      this.Workbooks,
      this.urlParamsFn,
      { owner: ownerId },
      SiteUserPlace.ExcludeFilterKeys));

    place.addSubplace(Subplace.create(
      'views',
      ViewContentType,
      this.Views,
      this.urlParamsFn,
      { owner: ownerId },
      SiteUserPlace.ExcludeFilterKeys));

    place.addSubplace(Subplace.create(
      'datasources',
      DatasourceContentType,
      this.DataSources,
      this.urlParamsFn,
      { owner: ownerId, showEmbedded: 'false' },
      SiteUserPlace.ExcludeFilterKeys));

    if (this.serverService.flowsOnSiteEnabled()) {
      place.addSubplace(Subplace.create(
        'flows',
        FlowContentType,
        this.Flows,
        this.urlParamsFn,
        { owner: ownerId},
        SiteUserPlace.ExcludeFilterKeys));
    }

    place.addSubplace(Subplace.create(
      'connections',
      DataConnectionContentType,
      this.DataConnections,
      this.urlParamsFn,
      // Since, in the current UX, data connections are listed under the data sources tab,
      // data connections should always be filtered to those contained by a data source
      // (not those contained by a flow), whether flows are enabled or not.
      FeatureFlags.maestroServerV1Enabled
        ? { containerType: 'datasource', owner: ownerId, showEmbedded: 'false' }
        : { owner: ownerId, showEmbedded: 'false' },
      SiteUserPlace.ExcludeFilterKeys));

    place.addSubplace(Subplace.create(
      'subscriptions',
      SubscriptionContentType,
      this.Subscriptions,
      this.urlParamsFn,
      { owner: ownerId },
      SiteUserPlace.ExcludeFilterKeys));

    if (this.serverService.isDataAlertsEnabled()) {
      place.addSubplace(Subplace.create(
        'dataAlerts',
        DataAlertContentType,
        this.DataAlerts,
        this.urlParamsFn,
        { user: ownerId },
        SiteUserPlace.ExcludeFilterKeys
      ));
    }

    return place;
  }

  public empty(): IPlace {
    const place = new Place('siteUser');

    // Filters are omitted because it would be extra work to make them work
    // and they serve no purpose because the subplaces are always empty.
    place.addSubplace(Subplace.empty(
      'projects',
      _.omit(ProjectContentType, 'filterKeys'),
      this.$q));

    place.addSubplace(Subplace.empty(
      'workbooks',
      _.omit(WorkbookContentType, 'filterKeys'),
      this.$q));

    place.addSubplace(Subplace.empty(
      'views',
      _.omit(ViewContentType, 'filterKeys'),
      this.$q));

    place.addSubplace(Subplace.empty(
      'datasources',
      _.omit(DatasourceContentType, 'filterKeys'),
      this.$q));

    place.addSubplace(Subplace.empty(
      'connections',
      _.omit(DataConnectionContentType, 'filterKeys'),
      this.$q));

    place.addSubplace(Subplace.empty(
      'subscriptions',
      _.omit(SubscriptionContentType, 'filterKeys'),
      this.$q));

    if (FeatureFlags.alertOnThresholdConditionEnabled) {
      place.addSubplace(Subplace.empty(
        'dataAlerts',
        _.omit(DataAlertContentType, 'filterKeys'),
        this.$q));
    }

    return place;
  }

}

Module.instance().service('SiteUserPlace', SiteUserPlace);
